/**
 * Lekka warstwa analityczna (Google Analytics 4 / gtag.js).
 *
 * Measurement ID pochodzi z konektora Google Analytics
 * (`VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY`). Jeśli konektor nie jest
 * jeszcze podpięty, wszystkie funkcje działają jako bezpieczny no-op,
 * a w trybie DEV logują zdarzenia do konsoli.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const MEASUREMENT_ID =
  (import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY as string | undefined) ?? "";

export const analyticsEnabled = Boolean(MEASUREMENT_ID);

let initialized = false;

export function initAnalytics() {
  if (initialized || typeof document === "undefined") return;
  initialized = true;

  if (!analyticsEnabled) return;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };
  window.gtag("js", new Date());
  // Page_view wysyłamy ręcznie przy każdej zmianie trasy (SPA).
  window.gtag("config", MEASUREMENT_ID, { send_page_view: false });
}

export function trackEvent(name: string, params: Record<string, unknown> = {}) {
  if (import.meta.env.DEV) {
    console.info("[analytics]", name, params);
  }
  if (!analyticsEnabled || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", name, params);
}

export function trackPageView(path: string, title?: string) {
  trackEvent("page_view", {
    page_path: path,
    page_location: typeof window !== "undefined" ? window.location.href : path,
    page_title: title ?? (typeof document !== "undefined" ? document.title : undefined),
  });
}

/** Klik w przycisk / link CTA. */
export function trackCtaClick(label: string, target?: string, location?: string) {
  trackEvent("cta_click", { cta_label: label, cta_target: target, cta_location: location });
}

/** Wysłanie formularza kontaktowego. */
export function trackFormSubmit(formName: string, params: Record<string, unknown> = {}) {
  trackEvent("form_submit", { form_name: formName, ...params });
}

/** Nieudana próba wysyłki formularza. */
export function trackFormError(formName: string, reason?: string) {
  trackEvent("form_error", { form_name: formName, reason });
}

/** Przewinięcie do sekcji strony (pierwsze wejście w viewport). */
export function trackSectionView(sectionId: string) {
  trackEvent("section_view", { section_id: sectionId });
}
